import React, { useEffect, useState } from 'react';
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from 'react-beautiful-dnd';
import { useParams, Link, generatePath, useHistory } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button,
  AddIcon,
  Badge,
  Row,
  Col,
  DragIndicatorIcon,
  Paragraph,
  LoadingOverlay,
  Dropdown,
  Modal,
  FormItem,
  Input,
  FormContainer,
  Dialog,
  EditOutlinedIcon,
  Loader,
  Radio,
  Heading,
  WarningIcon,
} from 'elmo-elements';
import {
  useAddFrameMutation,
  useGetElearningQuery,
  useGetFramesQuery,
  useGetFavouriteTemplatesQuery,
  useRepositionFrameMutation,
} from '@/modules/services';
import { FrameDataType } from '@/modules/types/frame';
import { useUpdateElearningMutation } from '@/modules/services/myElearnings';
import { RepositionEntityType } from '@/modules/types/helper';
import ModuleRoutes, {
  ModuleSlideRoutes,
  ElearningParamType,
} from '@/pages/Module/routes';

import './FrameNav.scss';
import config from '@/lib/config';
import { getNewFramePosition } from '@/lib/util';
import { getNavSlots } from '@/lib/elearningFrameNavSlots';
import { useDispatch } from 'react-redux';
import { actions as flashMessageActions } from '@/modules/reducers/flashMessage';

export interface SideItemType {
  id: string;
  type: 'unit' | 'frame';
  questionNumber: number;
  title: string;
  guardrailBlocked?: boolean;
}

interface UnitFormType {
  title: string;
}

interface AddButtonProps {
  onClick: (isOpen: boolean) => void;
  isAddSectionTitleDisabled: boolean;
}

interface addFrameFormDataType {
  title: string;
  isVideoFrame: boolean;
}

interface FrameTemplate {
  id: string;
  value: string;
  label: string;
  img: string;
  content?: string;
}

// This is the template not in the database
const defaultTemplateId = '00000000-0000-0000-0000-000000000000';

const defaultTemplate: FrameTemplate = {
  id: defaultTemplateId,
  value: 'blank',
  label: 'Blank',
  img: 'img-blank.svg',
};

const addFrameSchema = yup.object().shape({
  title: yup.string().max(255).trim().required('Slide name is required'),
  isVideoFrame: yup
    .boolean()
    .default(false)
    .required('Slide content type is required'),
});

const schema = yup.object().shape({
  title: yup.string().trim().max(255).required('Section title is required'),
});

function stringifyUnits(siteItems: SideItemType[]) {
  // @todo unit test
  let frameIndex = 0;
  const updatedUnits: string[] = [];
  siteItems.forEach((sideItem) => {
    if (sideItem.type === 'frame') frameIndex += 1;

    if (sideItem.type === 'unit') {
      const unitIndex = updatedUnits.findIndex((unit) =>
        unit.includes(`${frameIndex}#:`),
      );
      const updatedUnit = `${frameIndex + (unitIndex !== -1 ? 1 : 0)}#:${
        sideItem.title
      }`;
      updatedUnits.push(updatedUnit);
    }
  });

  return updatedUnits.join(';');
}

export function getSideItems(
  elearningUnits: string | undefined,
  frameData: FrameDataType[] | undefined,
) {
  // @todo unit test
  if (!frameData) return [];
  const slots = getNavSlots(elearningUnits, frameData);
  const sideItems: SideItemType[] = [];
  slots.forEach((slot, i) => {
    if (slot.unitTitle) {
      sideItems.push({
        id: `${i}`,
        questionNumber: i,
        type: 'unit',
        title: slot.unitTitle,
      });
    }
    if (slot.frame) {
      sideItems.push({
        id: slot.frame.id,
        type: 'frame',
        questionNumber: i,
        title: `${slot.frame.title}`,
        // Warning marker covers both placeholder kinds (blocked + generation failed).
        guardrailBlocked: !!(
          slot.frame.frameMetadata?.guardrailBlocked ||
          slot.frame.frameMetadata?.generationFailed
        ),
      });
    }
  });
  return sideItems;
}

function rearrangeSideItems(sideItems: SideItemType[], result: DropResult) {
  // @todo unit test
  if (!result.destination) return sideItems;

  const destinationIndex = result.destination.index;
  const [type, itemIndexString, id] = result.draggableId.split('_');
  const itemIndex = parseInt(itemIndexString, 10);
  const updatedSideItems = [...sideItems];

  if (itemIndex > destinationIndex) {
    updatedSideItems.splice(destinationIndex, 0, sideItems[itemIndex]);
    updatedSideItems.splice(itemIndex + 1, 1);
  } else {
    updatedSideItems.splice(destinationIndex + 1, 0, sideItems[itemIndex]);
    updatedSideItems.splice(itemIndex, 1);
  }

  return updatedSideItems;
}

function getFramePositions(updatedSideItems: SideItemType[]) {
  // @todo unit test
  const updatedFrames: RepositionEntityType[] = [];

  let updatedFrameCount = 0;
  updatedSideItems.map((sideItem, sideItemIndex) => {
    if (sideItem.type === 'frame') {
      updatedFrames.push({
        id: sideItem.id,
        position: updatedFrameCount,
      });
      updatedFrameCount += 1;
    }
    return false;
  });
  return updatedFrames;
}

const AddButton = ({ onClick, isAddSectionTitleDisabled }: AddButtonProps) => {
  const titleRef = React.useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const history = useHistory();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const { elearningId } = useParams<ElearningParamType>();
  const [addFrameQuery, addFrameStatus] = useAddFrameMutation();
  const { data: templatesData } = useGetFavouriteTemplatesQuery();
  const [selectedTemplate, setSelectedTemplate] =
    useState<FrameTemplate>(defaultTemplate);
  const frames = useGetFramesQuery({ elearningId });

  const {
    reset,
    setValue,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<addFrameFormDataType>({
    resolver: yupResolver(addFrameSchema),
    defaultValues: {
      title: '',
      isVideoFrame: false,
    },
  });

  if (!_.isEmpty(errors.title)) {
    if (titleRef.current) {
      titleRef.current.focus();
    }
  }

  const title = watch('title');

  const onAddSlide = async (data: addFrameFormDataType) => {
    try {
      const result = await addFrameQuery({
        title: data.title,
        elearningId,
        frameMetadata: {
          position: getNewFramePosition(frames?.data ?? []),
          mute: 2,
          swipeNavigation: 1,
          disabled: 0,
          backgroundColor: '',
          bgImage: '',
          bgVideo: '',
          sound: '',
          backgroundDisplay: null,
        },
        isVideoFrame: data.isVideoFrame,
        ...(selectedTemplate.content && {
          content: selectedTemplate.content,
        }),
        ...(selectedTemplate.id !== defaultTemplateId && {
          templateId: selectedTemplate.id,
        }),
      }).unwrap();

      history.push(
        generatePath(ModuleSlideRoutes.path, {
          elearningId,
          frameId: result.id,
        }),
      );

      dispatch({
        type: flashMessageActions.addFlashMessage,
        payload: {
          isActive: true,
          message: 'Successfully Added slide.',
        },
      });
    } catch (error) {
      dispatch({
        type: flashMessageActions.addFlashMessage,
        payload: {
          isActive: true,
          message: 'Something went wrong',
        },
      });
    }

    setIsOpen(false);
    reset();
    setSelectedTemplate(defaultTemplate);
  };

  const handleTemplateClick = (template: FrameTemplate) => {
    const isVideo = template.value === 'video';

    setValue('isVideoFrame', isVideo);
    setSelectedTemplate(template);
  };

  return (
    <>
      <Modal
        className="add-slide-modal"
        type="large"
        isOpened={isOpen}
        closeModal={() => {
          setIsOpen(false);
        }}
        title="New slide"
        closeLabel="Close"
        primaryButton={
          <Button
            isUppercase={false}
            type={title ? 'primary' : 'default'}
            onClick={handleSubmit(onAddSlide)}
          >
            Save
          </Button>
        }
      >
        <Row>
          <Col className="input-container" span={24}>
            <FormItem
              label="Slide name*"
              message={errors.title?.message}
              status={errors.title ? 'error' : 'default'}
            >
              <Input
                value={`${title}`}
                name="title"
                placeholder="New slide"
                onChange={(event) => setValue('title', event.target.value)}
                ref={titleRef}
              />
            </FormItem>
          </Col>
        </Row>
        <Row>
          <Col className="input-container" span={24}>
            <FormItem
              label="Template*"
              message={errors.isVideoFrame?.message}
              status={errors.isVideoFrame ? 'error' : 'default'}
            >
              <section className="frame-template-container">
                <Heading
                  htmlTag="h3"
                  type="heading"
                  size="sm"
                  className="frame-template-heading"
                >
                  Layouts
                </Heading>
                <Row className="layout-container">
                  <Col className="slide-option" span={12} lg={8}>
                    <img
                      src={`${config.assetPath}img-blank.svg`}
                      alt="blank template"
                    />

                    <Radio
                      value="blank"
                      name="Blank"
                      checked={selectedTemplate.value === 'blank'}
                      onChange={() => handleTemplateClick(defaultTemplate)}
                    >
                      Blank
                    </Radio>
                  </Col>
                  {templatesData?.map(
                    ({ id, name, templateMetadata, content }) => {
                      const mappedTemplate: FrameTemplate = {
                        id,
                        value: name,
                        label: name,
                        img: templateMetadata.img,
                        content,
                      };

                      return (
                        <Col
                          key={mappedTemplate.id}
                          className="slide-option"
                          span={12}
                          lg={8}
                        >
                          <img
                            src={
                              templateMetadata.img
                                ? `${config.slideTemplatePath}${templateMetadata.img}`
                                : `${config.assetPath}img-blank.svg`
                            }
                            alt={name}
                          />
                          <Radio
                            value={name}
                            name={name}
                            checked={selectedTemplate.id === mappedTemplate.id}
                            onChange={() => handleTemplateClick(mappedTemplate)}
                          >
                            {name}
                          </Radio>
                        </Col>
                      );
                    },
                  )}
                </Row>
                <Heading
                  htmlTag="h3"
                  type="heading"
                  size="sm"
                  className="frame-template-heading"
                >
                  Media
                </Heading>
                <Row className="layout-container">
                  <Col className="slide-option" span={12} lg={8}>
                    <img
                      src={`${config.assetPath}img-video-slide-popup.svg`}
                      alt="video slide"
                    />

                    <Radio
                      value="video"
                      name="Video only"
                      checked={selectedTemplate.value === 'video'}
                      onChange={() =>
                        handleTemplateClick({
                          id: defaultTemplateId,
                          value: 'video',
                          img: 'img-video-slide-popup.svg',
                          label: 'Video',
                        })
                      }
                    >
                      Video only
                    </Radio>
                  </Col>
                </Row>
              </section>
            </FormItem>
          </Col>
        </Row>
      </Modal>
      <Dropdown
        text={
          <>
            <AddIcon />
            Add
          </>
        }
        ariaLabel="Add Slide/Section Title"
        icon={<div />}
        className="dropdown-add"
      >
        <Dropdown.Item
          ariaLabel="Add Slide"
          value="frame"
          onClick={() => {
            setIsOpen(true);
          }}
        >
          Add Slide
        </Dropdown.Item>
        <Dropdown.Item
          ariaLabel="Add Section Title"
          value="unit"
          onClick={() => {
            onClick(true);
          }}
          isDisabled={isAddSectionTitleDisabled}
        >
          Add Section Title
        </Dropdown.Item>
      </Dropdown>
    </>
  );
};

const FrameNav = () => {
  const sidebarRef = React.useRef<HTMLDivElement>(null);
  const { elearningId, frameId } = useParams<ElearningParamType>();
  const frames = useGetFramesQuery({ elearningId });
  const elearning = useGetElearningQuery(elearningId);

  const [sideItems, setSideItems] = useState<SideItemType[]>(
    getSideItems(elearning.data?.units, frames.data),
  );

  const [updateElearning, updateElearningStatus] = useUpdateElearningMutation();
  const [repositionFrame, repositionFrameStatus] = useRepositionFrameMutation();

  const [unitModalOpen, setUnitModalOpen] = useState(false);
  const [currentUnitIndex, setCurrentUnitIndex] = useState<number | null>(null);

  useEffect(() => {
    if (frames.data)
      setSideItems(getSideItems(elearning.data?.units, frames.data));
  }, [frames.data]);

  useEffect(() => {
    const focusedFrameId = frameId || '';
    const dndFrameRef = document.querySelector(
      `[data-rbd-draggable-id$="${focusedFrameId}"]`,
    );

    if (dndFrameRef) {
      dndFrameRef.scrollIntoView();
    }
  }, [frameId, sideItems]);

  const {
    register,
    watch,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
  } = useForm<UnitFormType>({
    resolver: yupResolver(schema),
    defaultValues: {
      title: currentUnitIndex !== null ? sideItems[currentUnitIndex].title : '',
    },
  });

  if (elearning.isLoading || frames.isLoading || !elearning.data) {
    return <Loader type="bullet" />;
  }

  const handleValueTargetChange = (e: any, name: string) => {
    setValue(name as keyof UnitFormType, e.target.value);
  };

  const updateUnits = (siteItems: SideItemType[]) => {
    const updatedUnits = stringifyUnits(siteItems);
    updateElearning({
      id: elearningId,
      units: updatedUnits,
    });
  };

  const handleCloseModal = () => {
    setCurrentUnitIndex(null);
    setUnitModalOpen(false);
    reset();
  };

  async function onSubmit(formData: UnitFormType) {
    let updatedSideItems: SideItemType[] = [];
    if (currentUnitIndex === null) {
      updatedSideItems = [
        ...sideItems,
        {
          id: `${sideItems.length}`,
          type: 'unit',
          questionNumber: sideItems.length,
          title: formData.title,
        },
      ];
    } else {
      updatedSideItems = [...sideItems];
      updatedSideItems[currentUnitIndex] = {
        ...updatedSideItems[currentUnitIndex],
        title: formData.title,
      };
    }
    updateUnits(updatedSideItems);

    await setSideItems(updatedSideItems);

    handleCloseModal();
  }

  const linkSlides = generatePath(ModuleRoutes.path, {
    elearningId,
  });

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return false;

    const updatedSideItems = rearrangeSideItems(sideItems, result);

    setSideItems(updatedSideItems);

    updateUnits(updatedSideItems);

    const updatedFrames = getFramePositions(updatedSideItems);

    repositionFrame({
      elearningId,
      framePositions: updatedFrames,
    });

    return true;
  };

  const handleEditUnit = (index: number) => {
    setCurrentUnitIndex(index);
    setUnitModalOpen(true);
    setValue('title', sideItems[index].title);
  };

  const handleDeleteUnit = () => {
    if (currentUnitIndex === null) return false;
    const updatedSideItems = [...sideItems];
    updatedSideItems.splice(currentUnitIndex, 1);
    setSideItems(updatedSideItems);
    updateUnits(updatedSideItems);

    handleCloseModal();

    return false;
  };

  return (
    <div className="frame-nav">
      <Row
        isNoGutters={true}
        alignItems="center"
        className="py-3 frame-nav__header"
      >
        <Col span={12} className="pl-3">
          <Link to={linkSlides} className="btn-slides">
            Slides <Badge type="outlined">{frames.data?.length}</Badge>
          </Link>
        </Col>
        <Col span={12}>
          <AddButton
            isAddSectionTitleDisabled={!frames.data?.length}
            onClick={setUnitModalOpen}
          />
        </Col>
      </Row>

      <div className="frame-nav-list__wrapper" ref={sidebarRef}>
        <LoadingOverlay
          isLoading={
            updateElearningStatus.isLoading || repositionFrameStatus.isLoading
          }
        >
          <nav className="frame-nav-list">
            <DragDropContext onDragEnd={(result) => onDragEnd(result)}>
              <Droppable droppableId="droppable">
                {(provided) => (
                  <div {...provided.droppableProps} ref={provided.innerRef}>
                    {sideItems.map((sideItem, index) => (
                      <Draggable
                        key={sideItem.id}
                        draggableId={`${sideItem.type}_${index}_${sideItem.id}`}
                        index={index}
                      >
                        {(providedDrag) => (
                          <div
                            {...providedDrag.draggableProps}
                            ref={providedDrag.innerRef}
                          >
                            <Row
                              key={`SlideItem-${index}`}
                              role="row"
                              isNoGutters={true}
                              wrap="nowrap"
                              className={`slideItem-row ${
                                sideItem.id === frameId && 'is-active'
                              } ${sideItem.type === 'unit' && 'is-unit'}`}
                            >
                              <Col
                                align="center"
                                role="cell"
                                isAuto={true}
                                className="d-flex"
                              >
                                <div
                                  {...providedDrag.dragHandleProps}
                                  className="drag-icon mr-2 float-left"
                                >
                                  <DragIndicatorIcon />

                                  <Paragraph
                                    color="gray"
                                    size="sm"
                                    isBold={sideItem.type === 'unit'}
                                  >
                                    <span>
                                      {sideItem.type === 'unit' ? (
                                        sideItem.title
                                      ) : (
                                        <Link
                                          to={generatePath(
                                            ModuleSlideRoutes.path,
                                            {
                                              elearningId,
                                              frameId: sideItem.id,
                                            },
                                          )}
                                        >
                                          <span
                                            style={{
                                              display: 'inline-flex',
                                              alignItems: 'center',
                                            }}
                                          >
                                            {`${sideItem.questionNumber}. ${sideItem.title}`}
                                            {sideItem.guardrailBlocked && (
                                              <span
                                                style={{
                                                  color: '#f5a623',
                                                  display: 'inline-flex',
                                                  marginLeft: 4,
                                                }}
                                              >
                                                <WarningIcon
                                                  size="sm"
                                                  title="AI safety filter blocked this slide"
                                                />
                                              </span>
                                            )}
                                          </span>
                                        </Link>
                                      )}
                                    </span>
                                  </Paragraph>
                                </div>
                              </Col>
                              {sideItem.type === 'unit' && (
                                <Col
                                  isAuto={true}
                                  align="center"
                                  role="cell"
                                  grow={0}
                                >
                                  <Button
                                    icon={<EditOutlinedIcon />}
                                    onClick={() => {
                                      handleEditUnit(index);
                                    }}
                                    role="button"
                                    ariaLabel="Edit Section Title"
                                  />
                                </Col>
                              )}
                            </Row>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </nav>
        </LoadingOverlay>
      </div>

      <Modal
        isOpened={unitModalOpen}
        title={
          currentUnitIndex !== null ? 'Edit Section Title' : 'Add Section Title'
        }
        closeModal={handleCloseModal}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <FormContainer>
            <FormItem
              label="Title"
              message={errors.title && errors.title?.message}
              status={errors.title ? 'error' : 'default'}
            >
              <Input
                {...register('title', {})}
                onChange={(e) => handleValueTargetChange(e, 'title')}
                value={watch('title')}
                ariaLabel="Section Title"
              />
            </FormItem>
          </FormContainer>
          <div>
            {currentUnitIndex !== null && (
              <Button
                htmlType="button"
                type="danger"
                onClick={(e) => {
                  e.preventDefault();
                  Dialog.confirm({
                    title: 'Are you sure want to remove this section title?',
                    onOk() {
                      handleDeleteUnit();
                    },
                  });
                }}
                className="mr-2"
              >
                Delete
              </Button>
            )}
            <Button htmlType="submit" type="primary" isUppercase={false}>
              Save
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default FrameNav;
